import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PageHeader } from '../../v2/js/components/PageHeader';
import {
  ConnectedFacility,
  ConnectedFacilityService,
  ProvidersService,
  UserTypeEnum,
} from '../api';
import { useConfirmationModal } from '../app/components/ConfirmationModal';
import { ConnectionRequestCard } from '../app/components/ConnectionRequestCard/ConnectionRequestCard';
import { RejectConnectionDialog } from '../app/components/ConnectionRequestCard/RejectConnectionDialog';
import { ReviewConnectionDialog } from '../app/components/ConnectionRequestCard/ReviewConnectionDialog';
import { useFillQuestionnaireDialog } from '../app/components/FillQuestionnaireDialog';
import { ReviewAvailability } from '../app/components/ReviewAvailability';
import { useCurrentUser } from '../app/hooks/auth';
import { connectionRequestsQueryKeys } from '../app/query-keys-factories/connection-requests';
import { healthcareEntityFacilityQueryKeys } from '../app/query-keys-factories/facilities';
import { userQueryKeys } from '../app/query-keys-factories/user';
import { handleApiError } from '../app/utils/errorhandling';

export const ConnectionRequestDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const userIsProvider = user?.user_type === UserTypeEnum.PROVIDER;

  // State for modals and dialogs
  const [showReviewAvailabilityConfirmation, setShowReviewAvailabilityConfirmation] =
    useState(false);
  const [reviewedConnectionRequest, setReviewedConnectionRequest] =
    useState<ConnectedFacility | null>(null);
  const [rejectedConnectionRequest, setRejectedConnectionRequest] =
    useState<ConnectedFacility | null>(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractText, setContractText] = useState<string | null>(null);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [isSigningContract, setIsSigningContract] = useState(false);
  const [isDownloadingContract, setIsDownloadingContract] = useState(false);
  const [signedFirstName, setSignedFirstName] = useState('');
  const [signedLastName, setSignedLastName] = useState('');

  // Confirmation modals
  const { ConfirmationModal, showConfirmationModal } = useConfirmationModal();
  const { ConfirmationModal: CancelConfirmation, showConfirmationModal: showCancelConfirmation } =
    useConfirmationModal();
  const { FillQuestionnaireDialog, showFillQuestionnaireDialog } = useFillQuestionnaireDialog();

  const {
    data: connectionRequest,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['connected-facility', id],
    queryFn: () => ConnectedFacilityService.connectedFacilityRetrieve({ id: id! }),
    enabled: !!id,
  });

  // Mutations
  const mutationAcceptConnection = useMutation({
    mutationFn: async ({ connectedFacility }: { connectedFacility: string }) => {
      return ConnectedFacilityService.connectedFacilityAcceptPartialUpdate({
        id: connectedFacility,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
      queryClient.invalidateQueries(userQueryKeys.notifications());
      queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
      queryClient.invalidateQueries(['connected-facility', id]);
    },
  });

  const mutationCancelConnection = useMutation({
    mutationFn: async ({ connectedFacility }: { connectedFacility: string }) => {
      return ConnectedFacilityService.connectedFacilityCancelPartialUpdate({
        id: connectedFacility,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
      queryClient.invalidateQueries(userQueryKeys.notifications());
      queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
    },
  });

  // Handler functions
  const submitAcceptedRequest = async (connectedFacility: ConnectedFacility) => {
    if (userIsProvider && !user?.completed_questionnaire) {
      const shouldNavigate = await showFillQuestionnaireDialog();
      if (shouldNavigate) {
        navigate(
          `/app/provider/questionnaire/${user?.provider_id}?next=/app/provider/connection-requests`
        );
        return;
      }
    }

    const accepted = await showConfirmationModal();
    if (!accepted) {
      return;
    }

    try {
      await mutationAcceptConnection.mutateAsync({
        connectedFacility: connectedFacility.id,
      });
      toast.success('This connection request is complete.');

      setShowReviewAvailabilityConfirmation(true);
      setReviewedConnectionRequest(null);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleAccept = async (connectionRequest: ConnectedFacility) => {
    await submitAcceptedRequest(connectionRequest);
  };

  const handleReject = (data: ConnectedFacility) => {
    setRejectedConnectionRequest(data);
  };

  const submitCanceledRequest = async (connectedFacilityId: string) => {
    const canceled = await showCancelConfirmation();
    if (!canceled) {
      return;
    }

    try {
      await mutationCancelConnection.mutateAsync({
        connectedFacility: connectedFacilityId,
      });
      toast.success('This connection request has been canceled.');
      setReviewedConnectionRequest(null);
    } catch (error) {
      handleApiError(error, {
        messagePrefix: 'Error canceling connection request',
      });
    }
  };

  const handleCancel = (data: ConnectedFacility) => {
    submitCanceledRequest(data.id);
  };

  // Contract handling
  const handleContractRetrieveClick = async () => {
    if (!user?.provider_id) return;

    setIsLoadingContract(true);
    setShowContractModal(true);

    try {
      const xTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await ProvidersService.providersContractRetrieve({
        id: user.provider_id,
        xTz,
      });
      setContractText(response.text);
    } catch (error) {
      handleApiError(error, {
        messageOverride: 'Failed to load the working agreement. Please try again later.',
      });
      setShowContractModal(false);
    } finally {
      setIsLoadingContract(false);
    }
  };

  const handleDownloadContract = async () => {
    if (!user?.provider_id) return;

    setIsDownloadingContract(true);
    try {
      const xTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await ProvidersService.providersContractRetrieve({
        id: user.provider_id,
        xTz,
      });

      if (response.pdf) {
        // Create a blob from the base64 PDF
        const byteCharacters = atob(response.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);
        // Create a temporary link element
        const link = document.createElement('a');
        link.href = url;
        link.download = 'professional-services-agreement.pdf';
        // Append to body, click, and cleanup
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      handleApiError(error, { messageOverride: 'Error downloading PDF. Please try again.' });
    } finally {
      setIsDownloadingContract(false);
    }
  };

  const handleContractAgree = async () => {
    if (!user?.provider_id) return;

    setIsSigningContract(true);
    try {
      await ProvidersService.providersContractCreate({
        id: user.provider_id,
        xTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        requestBody: {
          signed_first_name: signedFirstName,
          signed_last_name: signedLastName,
        },
      });
      setShowContractModal(false);
      toast.success('Professional Services Agreement signed successfully.');
      queryClient.invalidateQueries({ queryKey: ['user', 'detail', 'current'] });
    } catch (error) {
      handleApiError(error, {
        messageOverride:
          'Failed to sign the Professional Services Agreement. Please try again later.',
      });
    } finally {
      setIsSigningContract(false);
    }
  };

  const onCloseReviewAvailability = (result: boolean) => {
    setShowReviewAvailabilityConfirmation(false);

    if (result) {
      navigate('/app/provider/profile');
    }
  };

  const closeHandleConnectionDialog = (result: boolean) => {
    if (!reviewedConnectionRequest) return;

    if (result) {
      handleAccept(reviewedConnectionRequest);
    } else {
      // Only set to null if user closes dialog without accepting
      setReviewedConnectionRequest(null);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !connectionRequest) {
    return <div>Error loading connection request details</div>;
  }

  return (
    <>
      <PageHeader
        backUrl="/app/provider/connection-requests"
        title="Connection Requests"
        buttonHtml={null}
      />
      <div className="container mt-4">
        <ConnectionRequestCard
          connectionRequest={connectionRequest}
          onAccept={handleAccept}
          onReject={handleReject}
          onCancel={handleCancel}
          needsContractSigned={user?.needs_contract_signed}
          onOpenContractModal={handleContractRetrieveClick}
        />
      </div>

      {/* Modals and Dialogs */}
      <ReviewAvailability
        show={showReviewAvailabilityConfirmation}
        onHide={onCloseReviewAvailability}
      />
      <FillQuestionnaireDialog />

      {reviewedConnectionRequest && (
        <ReviewConnectionDialog
          onSubmit={submitAcceptedRequest}
          connectionRequest={reviewedConnectionRequest}
          show={true}
          closeDialog={closeHandleConnectionDialog}
          onAccept={(connectionRequest) => {
            submitAcceptedRequest(connectionRequest);
          }}
          onReject={(connectionRequest) => {
            setRejectedConnectionRequest(connectionRequest);
          }}
          onCancel={(connectionRequest) => {
            handleCancel(connectionRequest);
          }}
          needsContractSigned={user?.needs_contract_signed}
          onOpenContractModal={handleContractRetrieveClick}
        />
      )}

      {rejectedConnectionRequest && (
        <RejectConnectionDialog
          connectionRequest={rejectedConnectionRequest}
          show={true}
          closeDialog={() => setRejectedConnectionRequest(null)}
          connectionId={id}
          onRejectSuccess={() => setRejectedConnectionRequest(null)}
        />
      )}

      <ConfirmationModal
        title="Connection Agreement"
        htmlText={
          <>
            <h5 className="text-section-name--dark-brand mb-2">
              {reviewedConnectionRequest?.facility.name}
            </h5>
            <div className="mt-2">
              <p>
                Accepting the connection indicates you are interested in sharing your contact
                information and discussing the opportunity further but does not guarantee commitment
                by either party.
              </p>
              <p>Next steps will include:</p>
              <ul style={{ listStyleType: 'disc' }}>
                <li style={{ paddingBottom: '10px' }}>
                  The organization verifying that you do not currently have a relationship
                </li>
                <li style={{ paddingBottom: '10px' }}>
                  The facility reviewing your profile for approval
                </li>
                <li style={{ paddingBottom: '10px' }}>
                  A call with the Clinical Leader where you can ask questions and share what makes
                  you a great fit
                </li>
              </ul>
            </div>
            <div className="">
              <p>
                By accepting you <b>acknowledge and authorize</b>
              </p>
              <ul style={{ listStyleType: 'disc' }}>
                <li style={{ paddingBottom: '10px' }}>Initial name clearance process</li>
                <li style={{ paddingBottom: '10px' }}>
                  Facility presentation (once clearance received)
                </li>
                <li style={{ paddingBottom: '10px' }}>
                  Any shifts worked as a result of this connection will be compensated at the rates
                  listed
                </li>
              </ul>
            </div>
          </>
        }
      />

      <CancelConfirmation
        confirmText="Cancel"
        cancelVariant="outline-primary"
        cancelText="Exit"
        confirmVariant="danger"
        title="Cancel Connection Request"
        text="Are you sure you want to cancel this connection request?"
      />

      {/* Contract Modal */}
      <Modal show={showContractModal} onHide={() => setShowContractModal(false)} size="lg">
        <Modal.Header closeButton>
          <div>
            <Modal.Title>Professional Services Agreement</Modal.Title>
            <div className="text-muted small mt-1">
              {isDownloadingContract ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Downloading...
                </>
              ) : (
                <>
                  Please review and sign at the bottom of the page.{' '}
                  <button
                    onClick={handleDownloadContract}
                    className="border-0 bg-transparent p-0 text-decoration-underline text-color-primary"
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="fw-medium"> Click here to download </span>
                  </button>{' '}
                  and review.
                  <i className="bi bi-download ms-1" />
                </>
              )}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {isLoadingContract ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <>
              <div dangerouslySetInnerHTML={{ __html: contractText || '' }} />
              <div className="mt-4">
                <p className="mb-3 text-danger">
                  Please enter your name to sign and accept this agreement
                </p>
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          value={signedFirstName}
                          onChange={(e) => setSignedFirstName(e.target.value)}
                          placeholder="First Name"
                          required
                          disabled={isSigningContract}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          value={signedLastName}
                          onChange={(e) => setSignedLastName(e.target.value)}
                          placeholder="Last Name"
                          required
                          disabled={isSigningContract}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => setShowContractModal(false)}
            disabled={isSigningContract}
            tabIndex={-1}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleContractAgree}
            disabled={!signedFirstName || !signedLastName || isSigningContract}
          >
            {isSigningContract ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Signing...
              </>
            ) : (
              'Sign'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
